/**
 * ★★ WK — o funil do desenho aprovado: barras que FLUTUAM, sem trilho.
 *
 * ⚠ A primeira versão pôs um trilho cinza atrás de cada barra, e o dono
 * reprovou com a razão certa: trilho transforma funil em **barra de progresso**.
 * Barra de progresso diz "quanto falta para 100%"; funil diz "quanto sobrou do
 * degrau de cima". São leituras diferentes, e o fundo cinza sugeria a errada.
 *
 * Agora as barras flutuam no branco, centradas, ligadas por um conector vertical
 * fino — o afunilamento é a própria forma, sem moldura.
 *
 * ★ O tom de azul ESCURECE de cima para baixo, e o texto acompanha: nos dois
 * primeiros degraus o azul é claro e o número vai em azul-escuro; nos dois
 * últimos o fundo é escuro e o número vai em branco. Contraste legível nos
 * quatro.
 */

const pct1 = (v) => `${Number(v ?? 0).toFixed(1).replace('.', ',')}%`;

/** Do mais claro ao mais escuro — a ordem dos degraus (desenho aprovado). */
const TONS = [
  { fundo: '#bfdbfe', texto: '#0f2d6b' },
  { fundo: '#7aa9f4', texto: '#0f2d6b' },
  { fundo: '#3b76e0', texto: '#ffffff' },
  { fundo: '#17418f', texto: '#ffffff' },
];

/**
 * ★★ A ESCALA DO FUNIL — comprimida, e por quê.
 *
 * ⚠ Com escala LINEAR a barra de Leads tomava a largura inteira e esmagava as
 * outras três: medido em produção, 667 → 100 % e 47 → 7,0 %, com "Fecharam"
 * virando um talo ilegível. O dono reprovou.
 *
 * ★ A saída NÃO é mentir sobre o número (ele está impresso dentro da barra), é
 * comprimir a ESCALA: `largura = MAXIMO × √(valor / maior)`.
 *
 *   A raiz quadrada é **monótona** — quem tem mais nunca fica com barra menor —
 *   e aproxima os extremos sem inverter nada. Nos números reais de setembro:
 *
 *     Leads        667  linear 100,0 %   →  raiz  75,0 %
 *     Agendaram    177  linear  26,5 %   →  raiz  38,6 %
 *     Compareceram  62  linear   9,3 %   →  raiz  22,9 %
 *     Fecharam      47  linear   7,0 %   →  raiz  19,9 %
 *
 *   O afunilamento continua visível e "Fecharam" volta a caber o número.
 *
 * ★ `MAXIMO = 75 %` deixa respiro dos dois lados (a barra é centrada) e impede
 * que a maior encoste nas bordas do cartão.
 * ★ `PISO = 10 %` substitui o piso antigo de 2 %: 2 % de ~700 px são 14 px, que
 * não seguram o número. O piso só age em casos extremos (1 fechamento em 600
 * leads daria 2,9 % pela raiz), e **nunca inverte** — dois valores abaixo dele
 * empatam em largura, não trocam de ordem.
 */
export const MAXIMO_PCT = 75;
export const PISO_PCT = 10;

export function larguraDaBarra(valor, maior) {
  const v = Number(valor) || 0;
  if (v <= 0) return 0;
  const m = Math.max(1, Number(maior) || 0);
  return Math.max(PISO_PCT, MAXIMO_PCT * Math.sqrt(Math.min(1, v / m)));
}

export default function FunilBarras({ degraus = [] }) {
  const maximo = Math.max(1, ...degraus.map((d) => Number(d.valor) || 0));
  return (
    <div className="funil">
      {degraus.map((d, i) => {
        const valor = Number(d.valor) || 0;
        const largura = larguraDaBarra(valor, maximo);
        const tom = TONS[Math.min(i, TONS.length - 1)];
        return (
          <div key={d.id}>
            {/* o conector entre um degrau e o seguinte, com a taxa à direita */}
            {i > 0 && (
              <div className="funil-conector-linha">
                <div className="funil-conector-rotulo" />
                <div className="funil-conector-meio"><span className="funil-conector" /></div>
                {/**
                  * ★★ A TAXA ACIMA DE 100 % NÃO É IMPRESSA CRUA.
                  *
                  * Medido pelo dono em 23–30/09: Compareceram 14, Fecharam 24 →
                  * a tela escreveu "171,4% fecharam", que é impossível de ler.
                  * A causa é real e continua valendo: os degraus contam eventos
                  * DENTRO da janela, e quem fecha numa janela curta pode ter
                  * comparecido antes dela. ★ Decisão do dono: a conversão por
                  * degrau FICA — o que muda é a tela dizer o que está vendo.
                  */}
                <div className={d.acima ? 'funil-conector-taxa acima' : 'funil-conector-taxa'}>
                  {d.taxa === null || d.taxa === undefined ? '' : (
                    d.acima ? (
                      <>
                        <span className="funil-taxa-sinal">&gt;100%</span>
                        <span className="funil-taxa-explica">
                          parte de quem fechou compareceu antes desta janela
                          {d.foraDaJanela > 0 ? ` (${d.foraDaJanela})` : ''}
                        </span>
                      </>
                    ) : `${pct1(d.taxa)} ${d.verbo}`
                  )}
                </div>
              </div>
            )}
            <div className="funil-degrau">
              <div className="funil-rotulo">
                <div className="funil-nome">{d.rotulo}</div>
                <div className="funil-sub">{d.sub}</div>
              </div>
              <div className="funil-meio">
                <div
                  className="funil-barra"
                  style={{ width: `${largura}%`, background: tom.fundo }}
                  title={`${d.rotulo}: ${valor}`}
                >
                  <span className="funil-numero" style={{ color: tom.texto }}>{valor}</span>
                </div>
              </div>
              <div className="funil-vazio" />
            </div>
          </div>
        );
      })}
    </div>
  );
}
